import {
  ACQUISITION_DEFAULT_SOURCE,
  ACQUISITION_FIELDS,
  ACQUISITION_MANUAL_SOURCE,
  ACQUISITION_MAX_TARGET_MARGIN_PCT,
  ACQUISITION_OPTION_DEFAULTS,
  ACQUISITION_TARGET_MARGIN_SOURCE,
  acquisitionStartInputs,
  formatAmountInput,
  formatPercentInput,
  parseAmountInput,
  parsePercentInput,
  sameAcquisitionUnits,
  type AcquisitionField,
  type AcquisitionCommercialSplit,
  type AcquisitionCostsPayer,
  type AcquisitionInputs,
  type AcquisitionMode,
  type AcquisitionOptions,
  type AcquisitionPrefill,
  type AcquisitionSourceKey,
  type AcquisitionUnitRow,
} from '@brimo/shared';
import { formatNumber, parseDecimal } from '../format';
import type { CalculationVersion } from '../types';

/**
 * Draft model of Objekt anlegen 05 „Ankaufskalkulation" in Nico Kremer's model (D-351…D-357): his inputs with his
 * labels, grouped and ordered as in his calculator, the editable unit list and the auto note of a new version; since
 * his feedback of 01.11.2026 also „Rechenweg", „Kaufnebenkosten zahlt" and „Gewerbeanteil nach" (D-369…D-372).
 * The arithmetic is `acquisitionResultsOf` of @brimo/shared — the same code the API serializes.
 */

/** „Rechenweg" (D-370). */
export const MODE_ITEMS: { value: AcquisitionMode; label: string }[] = [
  { value: 'forward', label: 'Kaufpreis → Marge' },
  { value: 'target_margin', label: 'Zielmarge → Kaufpreis' },
];

/** „Kaufnebenkosten zahlt" (D-371). */
export const PAYER_ITEMS: { value: AcquisitionCostsPayer; label: string }[] = [
  { value: 'buyer', label: 'Käufer' },
  { value: 'seller', label: 'Verkäufer' },
];

/** „Gewerbeanteil nach" (D-372). */
export const SPLIT_ITEMS: { value: AcquisitionCommercialSplit; label: string }[] = [
  { value: 'area', label: 'Fläche' },
  { value: 'rent', label: 'Miete' },
];

/** The options of the draft; the target margin as its editing text. */
export interface OptionsDraft {
  mode: AcquisitionMode;
  targetMargin: string;
  acquisitionCostsPayer: AcquisitionCostsPayer;
  commercialSplit: AcquisitionCommercialSplit;
}

/** An option that names itself in the auto note. */
export type OptionKey = 'mode' | 'targetMarginPct' | 'acquisitionCostsPayer' | 'commercialSplit';

/** What changed against the saved state: one of Nico's inputs, the unit list or an option. */
export type CalculationChange = AcquisitionSourceKey | OptionKey;

const OPTION_NOTES: Record<OptionKey, string> = {
  mode: 'Rechenweg geändert',
  targetMarginPct: 'Zielmarge angepasst',
  acquisitionCostsPayer: 'Nebenkosten-Zahler geändert',
  commercialSplit: 'Gewerbeanteil angepasst',
};

const OPTION_KEYS: readonly OptionKey[] = [
  'mode',
  'targetMarginPct',
  'acquisitionCostsPayer',
  'commercialSplit',
];

function isOptionKey(key: CalculationChange): key is OptionKey {
  return (OPTION_KEYS as readonly string[]).includes(key);
}

/** „Zielmarge % auf GIK" text → percent; null = empty, undefined = unreadable or out of range. */
export function parseTargetMargin(text: string): number | null | undefined {
  if (text.trim() === '') return null;
  const parsed = parsePercentInput(text);
  if (parsed === null || parsed < 0 || parsed > ACQUISITION_MAX_TARGET_MARGIN_PCT) return undefined;
  return parsed;
}

/** The options of a draft as the API takes them (the target margin only in the target mode). */
export function optionsFromDraft(options: OptionsDraft): AcquisitionOptions {
  return {
    mode: options.mode,
    targetMarginPct:
      options.mode === 'target_margin' ? (parseTargetMargin(options.targetMargin) ?? null) : null,
    acquisitionCostsPayer: options.acquisitionCostsPayer,
    commercialSplit: options.commercialSplit,
  };
}

function optionsDraftOf(options: AcquisitionOptions): OptionsDraft {
  return {
    mode: options.mode,
    targetMargin: options.targetMarginPct === null ? '' : formatPercentInput(options.targetMarginPct),
    acquisitionCostsPayer: options.acquisitionCostsPayer,
    commercialSplit: options.commercialSplit,
  };
}

/**
 * The inputs a draft is computed and saved with: in the target mode the Kaufpreis is the solved „max. Kaufpreis"
 * (null while it cannot be solved), otherwise the typed one.
 */
export function effectiveInputs(
  inputs: AcquisitionInputs,
  options: AcquisitionOptions,
  maxPurchasePriceCents: number | null | undefined,
): AcquisitionInputs {
  return options.mode === 'target_margin'
    ? { ...inputs, purchasePriceCents: maxPurchasePriceCents ?? null }
    : inputs;
}

export type FieldUnit = 'eur' | 'pct' | 'eurPerSqm' | 'eurPerMonth' | 'eurPerSqmMonth' | 'months';

export interface FieldSpec {
  field: AcquisitionField;
  /** Nico's label. */
  label: string;
  unit: FieldUnit;
}

/** Nico's three input panels, in his order. */
export const CALCULATION_GROUPS: { id: string; label: string; fields: FieldSpec[] }[] = [
  {
    id: 'purchase',
    label: 'Einkauf / Gesamtkosten (GIK)',
    fields: [
      { field: 'purchasePriceCents', label: 'Kaufpreis (verhandelt)', unit: 'eur' },
      { field: 'brokerCommissionPct', label: 'Käufer-Maklercourtage', unit: 'pct' },
      { field: 'landTransferTaxPct', label: 'Grunderwerbsteuer', unit: 'pct' },
      { field: 'notaryPct', label: 'Notar / Grundbuch', unit: 'pct' },
      { field: 'capexCents', label: 'Cap-Ex / Sanierung', unit: 'eur' },
      { field: 'financingCostsCents', label: 'Finanzierungskosten', unit: 'eur' },
      { field: 'divisionCostsCents', label: 'Teilung / WEG', unit: 'eur' },
    ],
  },
  {
    id: 'sale',
    label: 'Verkauf',
    fields: [
      { field: 'targetSalePricePerSqmCents', label: 'Ziel-Verkaufspreis', unit: 'eurPerSqm' },
      { field: 'salesCommissionPct', label: 'Vertriebsprovision', unit: 'pct' },
    ],
  },
  {
    id: 'investor',
    label: 'Kapitalanlage-Parameter (je WE)',
    fields: [
      { field: 'interestPct', label: 'Sollzins p. a.', unit: 'pct' },
      { field: 'repaymentPct', label: 'Tilgung p. a.', unit: 'pct' },
      { field: 'depreciationPct', label: 'AfA-Satz p. a.', unit: 'pct' },
      { field: 'buildingSharePct', label: 'Gebäudeanteil', unit: 'pct' },
      { field: 'taxRatePct', label: 'Steuersatz', unit: 'pct' },
      { field: 'buyerAcquisitionCostsPct', label: 'Erwerbsnebenkosten Käufer', unit: 'pct' },
      { field: 'unitManagementCents', label: 'SEV-Verwaltung', unit: 'eurPerMonth' },
      { field: 'hoaManagementCents', label: 'WEG-Verwaltung', unit: 'eurPerMonth' },
      { field: 'maintenancePerSqmCents', label: 'Instandhaltung', unit: 'eurPerSqmMonth' },
      { field: 'otherCostsPerSqmCents', label: 'sonst. abs. NK', unit: 'eurPerSqmMonth' },
      { field: 'rentSubsidyPct', label: 'Mietsubvention', unit: 'pct' },
      { field: 'rentSubsidyMonths', label: 'Subventions-Monate', unit: 'months' },
    ],
  },
];

export const CALCULATION_FIELD_SPECS: FieldSpec[] = CALCULATION_GROUPS.flatMap((group) => group.fields);

const SPEC_BY_FIELD = new Map(CALCULATION_FIELD_SPECS.map((spec) => [spec.field, spec]));

export const UNIT_SUFFIX: Record<FieldUnit, string> = {
  eur: '€',
  pct: '%',
  eurPerSqm: '€/m²',
  eurPerMonth: '€/M',
  eurPerSqmMonth: '€/m²/M',
  months: 'Monate',
};

/** Label of a field or the unit list in notes and the source list. */
export function sourceLabel(key: AcquisitionSourceKey): string {
  return key === 'units' ? 'IST-Mietaufstellung' : (SPEC_BY_FIELD.get(key)?.label ?? key);
}

function unitOf(field: AcquisitionField): FieldUnit {
  return SPEC_BY_FIELD.get(field)?.unit ?? 'eur';
}

function isMoney(unit: FieldUnit): boolean {
  return unit === 'eur' || unit === 'eurPerSqm' || unit === 'eurPerMonth' || unit === 'eurPerSqmMonth';
}

/** Editing text of a field („3.150.000,00", „3,57", „36"). */
export function displayValue(field: AcquisitionField, value: number | null): string {
  if (value === null) return '';
  const unit = unitOf(field);
  if (isMoney(unit)) return formatAmountInput(value);
  if (unit === 'months') return String(value);
  return formatPercentInput(value);
}

/** Parsed value of a field: null = empty, undefined = unreadable. */
export function parseField(field: AcquisitionField, text: string): number | null | undefined {
  if (text.trim() === '') return null;
  const unit = unitOf(field);
  let parsed: number | null;
  if (isMoney(unit)) parsed = parseAmountInput(text);
  else if (unit === 'months') parsed = /^\d{1,3}$/.test(text.trim()) ? Number(text.trim()) : null;
  else parsed = parsePercentInput(text);
  if (parsed === null || parsed < 0) return undefined;
  if (unit === 'pct' && parsed > 100) return undefined;
  if (unit === 'months' && parsed > 600) return undefined;
  return parsed;
}

export type FieldDraft = Record<AcquisitionField, string>;

/** One editable row of the unit list. */
export interface UnitDraft {
  /** Stable React key of the row. */
  key: string;
  position: string;
  number: string;
  area: string;
  rent: string;
  residential: boolean;
}

export interface CalculationDraft {
  fields: FieldDraft;
  units: UnitDraft[];
  options: OptionsDraft;
}

let unitKeySeed = 0;
function nextUnitKey(): string {
  unitKeySeed += 1;
  return `unit-${unitKeySeed}`;
}

export function unitDraftOf(row: AcquisitionUnitRow): UnitDraft {
  return {
    key: nextUnitKey(),
    position: row.position,
    number: row.number,
    area: row.areaSqm === 0 ? '' : formatNumber(row.areaSqm, 2),
    rent: row.rentCents === 0 ? '' : formatAmountInput(row.rentCents),
    residential: row.residential,
  };
}

/** A new empty row („WE n"). */
export function emptyUnitDraft(index: number): UnitDraft {
  return {
    key: nextUnitKey(),
    position: '',
    number: `WE ${index + 1}`,
    area: '',
    rent: '',
    residential: true,
  };
}

/** Area text → m² with at most two decimals; null = empty, undefined = unreadable. */
export function parseArea(text: string): number | null | undefined {
  if (text.trim() === '') return null;
  const parsed = parseDecimal(text);
  if (parsed === null || parsed < 0 || parsed > 100_000) return undefined;
  if (Math.abs(parsed * 100 - Math.round(parsed * 100)) > 1e-6) return undefined;
  return parsed;
}

/** Rent text → cents; null = empty, undefined = unreadable. */
export function parseRent(text: string): number | null | undefined {
  if (text.trim() === '') return null;
  const parsed = parseAmountInput(text);
  return parsed === null || parsed < 0 ? undefined : parsed;
}

/** The unit list of a draft (unreadable or empty area/rent count as 0). */
export function unitsFromDraft(units: readonly UnitDraft[]): AcquisitionUnitRow[] {
  return units.map((row) => ({
    position: row.position.trim(),
    number: row.number.trim(),
    areaSqm: parseArea(row.area) ?? 0,
    rentCents: parseRent(row.rent) ?? 0,
    residential: row.residential,
  }));
}

/**
 * The start of the draft: the current version's inputs and units; without a version Nico's defaults with the
 * property's land transfer tax and the property's units (`prefill`).
 */
export function draftFrom(current: CalculationVersion | null, prefill: AcquisitionPrefill): CalculationDraft {
  const inputs = current?.inputs ?? acquisitionStartInputs(prefill.landTransferTaxPct);
  const units = current === null ? prefill.units : current.units;
  return {
    fields: Object.fromEntries(
      ACQUISITION_FIELDS.map((field) => [field, displayValue(field, inputs[field])]),
    ) as FieldDraft,
    units: units.map(unitDraftOf),
    options: optionsDraftOf(current?.options ?? ACQUISITION_OPTION_DEFAULTS),
  };
}

/** Parsed inputs of the draft; unreadable fields count as empty. */
export function inputsFromDraft(fields: FieldDraft): AcquisitionInputs {
  return Object.fromEntries(
    ACQUISITION_FIELDS.map((field) => [field, parseField(field, fields[field]) ?? null]),
  ) as AcquisitionInputs;
}

/** Fields, unit rows and the target margin (required in the target mode) that cannot be read. */
export function invalidOf(draft: CalculationDraft): {
  fields: AcquisitionField[];
  units: string[];
  targetMargin: boolean;
} {
  const targetMargin = parseTargetMargin(draft.options.targetMargin);
  return {
    targetMargin:
      draft.options.mode === 'target_margin' && (targetMargin === null || targetMargin === undefined),
    fields: ACQUISITION_FIELDS.filter(
      // the solved Kaufpreis of the target mode is not typed
      (field) =>
        !(field === 'purchasePriceCents' && draft.options.mode === 'target_margin') &&
        parseField(field, draft.fields[field]) === undefined,
    ),
    units: draft.units
      .filter(
        (row) =>
          parseArea(row.area) === undefined ||
          parseRent(row.rent) === undefined ||
          row.number.trim() === '' ||
          row.number.trim().length > 40 ||
          row.position.trim().length > 80,
      )
      .map((row) => row.key),
  };
}

/**
 * What differs from the saved state: against the current version, or — before the first version — against the
 * start values (defaults + prefill), so an untouched new calculation is not dirty. In the target mode the solved
 * Kaufpreis is not a change of its own (the target margin, the mode or the other inputs are).
 */
export function changedKeys(
  inputs: AcquisitionInputs,
  units: readonly AcquisitionUnitRow[],
  current: CalculationVersion | null,
  prefill: AcquisitionPrefill,
  options: AcquisitionOptions = ACQUISITION_OPTION_DEFAULTS,
): CalculationChange[] {
  const baseInputs = current?.inputs ?? acquisitionStartInputs(prefill.landTransferTaxPct);
  const baseUnits = current?.units ?? prefill.units;
  const baseOptions = current?.options ?? ACQUISITION_OPTION_DEFAULTS;
  const changed: CalculationChange[] = ACQUISITION_FIELDS.filter(
    (field) =>
      !(field === 'purchasePriceCents' && options.mode === 'target_margin') &&
      baseInputs[field] !== inputs[field],
  );
  if (!sameAcquisitionUnits(baseUnits, units)) changed.push('units');
  if (baseOptions.mode !== options.mode) changed.push('mode');
  else if (options.mode === 'target_margin' && baseOptions.targetMarginPct !== options.targetMarginPct)
    changed.push('targetMarginPct');
  if (baseOptions.acquisitionCostsPayer !== options.acquisitionCostsPayer)
    changed.push('acquisitionCostsPayer');
  if (baseOptions.commercialSplit !== options.commercialSplit) changed.push('commercialSplit');
  return changed;
}

/**
 * The sources of a new version for values nobody changed: before the first version the start values are
 * „Standardwert", the land transfer tax names its origin and the units come „aus Einheiten des Objekts"; later the
 * API keeps the previous source of every unchanged value itself.
 */
export function startSources(
  inputs: AcquisitionInputs,
  units: readonly AcquisitionUnitRow[],
  current: CalculationVersion | null,
  prefill: AcquisitionPrefill,
  changed: readonly CalculationChange[],
): Partial<Record<AcquisitionSourceKey, string>> {
  if (current !== null) return {};
  const sources: Partial<Record<AcquisitionSourceKey, string>> = {};
  for (const field of ACQUISITION_FIELDS) {
    if (inputs[field] === null || changed.includes(field)) continue;
    sources[field] =
      field === 'landTransferTaxPct' ? prefill.landTransferTaxSource : ACQUISITION_DEFAULT_SOURCE;
  }
  if (units.length > 0 && !changed.includes('units') && prefill.unitsSource !== null)
    sources.units = prefill.unitsSource;
  return sources;
}

/**
 * „Cap-Ex / Sanierung angepasst" · „Rechenweg geändert" · „Nebenkosten-Zahler geändert, Cap-Ex / Sanierung
 * angepasst" · „Kalkulation erfasst" · „Version gespeichert"; more than three changes → „Kalkulation angepasst".
 */
export function autoNote(changed: readonly CalculationChange[], current: CalculationVersion | null): string {
  if (changed.length === 0) return current === null ? 'Kalkulation erfasst' : 'Version gespeichert';
  if (current === null) return 'Kalkulation erfasst';
  if (changed.length > 3) return 'Kalkulation angepasst';
  const options = changed.filter(isOptionKey).map((key) => OPTION_NOTES[key]);
  const fields = changed.filter((key): key is AcquisitionSourceKey => !isOptionKey(key));
  return [...options, ...(fields.length > 0 ? [`${fields.map(sourceLabel).join(', ')} angepasst`] : [])].join(
    ', ',
  );
}

/** The source of a field while editing: changed values are „manuell", else the saved source. */
export function liveSource(
  key: AcquisitionSourceKey,
  hasValue: boolean,
  changed: readonly CalculationChange[],
  current: CalculationVersion | null,
  prefill: AcquisitionPrefill,
  mode: AcquisitionMode = 'forward',
): { source: string; manual: boolean } | null {
  if (!hasValue) return null;
  if (key === 'purchasePriceCents' && mode === 'target_margin')
    return { source: ACQUISITION_TARGET_MARGIN_SOURCE, manual: false };
  if (changed.includes(key)) return { source: ACQUISITION_MANUAL_SOURCE, manual: true };
  if (current !== null) return current.fieldSources[key] ?? null;
  if (key === 'units') return prefill.unitsSource ? { source: prefill.unitsSource, manual: false } : null;
  if (key === 'landTransferTaxPct') return { source: prefill.landTransferTaxSource, manual: false };
  return { source: ACQUISITION_DEFAULT_SOURCE, manual: false };
}
